import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {SettingsParkingLot, SettingsParkingRule} from '../types/data.types';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {Attachment} from '../types/email-data.types';
import {EmailService} from './email.service';
import {AccountPermissionsService} from './account-permissions.service';
import {ConstantsService} from './constants.service';
import {filter, map, tap} from 'rxjs/operators';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';
import {of} from 'rxjs/observable/of';

declare const toastr: any;
declare const swal: any;
declare const $: any;

@Injectable()
export class SettingsParkingPassesService {
    currentTabId = 'rules';
    parkingPassesData = {
        rules: {
            get: '/v1/parking_rule.json?expand=passTypes,parkingLots,staffNotificationSettings,staffNotificationSettings.userRoles,residentsNotificationSettings,passTypes.passTypeSchedule,passTypes.passTypeSchedule.scheduleDays,limitGroups,limitGroups.limitedPassTypes,createdBy,lotRule.parkingLot',
            post: '/v1/parking_rule.json',
            patch: '/v1/parking_rule/',
            remove: '/v1/parking_rule/',
            data: [],
            total: null
        },
        lots: {
            get: '/v1/parking_lot.json?expand=building,createdBy',
            post: '/v1/parking_lot.json',
            patch: '/v1/parking_lot/',
            remove: '/v1/parking_lot/',
            data: [],
            total: null
        }
    };
    onDataReceived: EventEmitter<any> = new EventEmitter();
    onDataSend: EventEmitter<any> = new EventEmitter();
    hideSpinner: EventEmitter<any> = new EventEmitter();
    showCreateModal = false;
    parkingLotsInMultiselectFormat: Array<any> = [];
    parkingLotsForFilter: Array<any> = [];
    isPassTypeModalShow = false;
    isDisabledDatesModalShow = false;
    isLegalAgreementModalShow = false;
    isPassNotificationModalShow = false;
    isDisclaimerModalShow = false;
    showDetailedView = false;
    selectedItemData: any;
    passTypeInPassTypeModal: any;
    spinnerShow = true;
    showServiceUnavailable = false;
    buildings: Array<any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    buldingsInMultiselectFormat: Array<any> = [];
    selectedItemFormattedBuildingsArray: Array<any> = [];
    selectedItemFormattedLotsArray: Array<any> = [];
    selectedItemFormattedPassesArray: Array<any> = [];
    modalSpinnerShow = false;
    serviceName: string = 'parking_pass';
    passNotificationModalStaffOrResident = '';
    passNotificationModalNotificationType = '';
    userRoles: Array<any> = [];
    createdStaffMultiselectArray: Array<any> = [];
    approvedStaffMultiselectArray: Array<any> = [];
    declinedStaffMultiselectArray: Array<any> = [];
    requestStaffMultiselectArray: Array<any> = [];
    passCreatedStaffCheckbox = false;
    passApprovedStaffCheckbox = false;
    passDeclinedStaffCheckbox = false;
    passRequestStaffCheckbox = false;
    passCreatedResidentCheckbox = false;
    passApprovedResidentCheckbox = false;
    passDeclinedResidentCheckbox = false;
    passRequestResidentCheckbox = false;

    constructor(public es: EmailService,
                public api: ApiClientService,
                public urlService: UrlService,
                public emptyRowService: EmptyRowService,
                public account: AccountPermissionsService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
    }

    getDataByQueryParams(scrollToTop?) {
        this.showServiceUnavailable = false;
        const tabId = this.urlService.queryParams['tab'];
        const url = this.parkingPassesData[tabId].get;
        const queryParams = this.urlService.getQueryParamsString();

        let model;
        if (tabId === 'rules') {
            model = SettingsParkingRule;
        } else if (tabId === 'lots') {
            model = SettingsParkingLot;
        }
        return this.api.getJson('domainParkingPass', `${url}${queryParams}`)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity((item) => {
                    if (item.building && item.building.bdId) {
                        item.building.id = item.building.bdId;
                    }
                    return model.fromExistingData(this.account.addPermissionsToEntity(item, this.serviceName));
                }),
                tap((response) => {
                    if (tabId === 'lots') {
                        this.parkingLotsInMultiselectFormat = this.parkingLotsConvertForMultiselect(response.entities);
                    }
                }),
                tap((res) => {
                    if (res.finished) {
                        this.onDataReceived.emit({id: tabId, data: res, scrollToTop: scrollToTop});
                        this.hideSpinner.emit();
                    } else {
                        this.onDataSend.emit();
                    }
                }),
                tap(
                    (res) => {
                        this.parkingPassesData[tabId].data = res.entities;
                        this.parkingPassesData[tabId].total = res.total;
                        this.emptyRowService.fillEmptyRow();
                        this.emptyNotificationArrays();
                    },
                    (error) => {
                        this.showServiceUnavailable = true;
                        this.hideSpinner.emit();
                        this.api.errorHandler(error, `Failed get Parking ${tabId} Settings`);
                    }
                ),
                filter(({finished}) => finished),
                tap(() => {
                    if (this.selectedItemData) {
                        this.selectedItemData = this.parkingPassesData[tabId].data.find(si => si.id == this.selectedItemData.id);
                        if (this.selectedItemData) {
                            this.selectedItemData.checked = true;
                            if (tabId == 'rules') {
                                this.selectedItemFormattedLotsArray = this.lotsParkingPassesConvertForMultiselect(this.selectedItemData.parkingLots);
                                this.selectedItemFormattedPassesArray = this.lotsParkingPassesConvertForMultiselect(this.selectedItemData.passTypes);
                            } else if (tabId == 'lots')
                                this.selectedItemFormattedBuildingsArray = this.buildingParkingPassesConvertForMultiselect(this.selectedItemData.building);
                        } else
                            this.showDetailedView = false;
                        if (tabId == 'rules')
                            this.formatNotificationSettingsRoles();
                    }
                }),
            );
    }

    getBuildings() {
        if (this.buildings.length) return of(this.buildings);
        const url = `/v1/buildings.json?expand=timeZone&order-by=name|asc`;
        return this.api.getInfiniteList('domainBD', url)
            .pipe(
                tap(
                    (res) => {
                        this.buildings = res.entities;
                        this.getFilteredBuildings();
                    },
                    (error) => this.api.errorHandler(error, 'Failed get buildings')
                ),
                map(r => r.entities)
            );
    }

    resetData() {
        for (const item in this.parkingPassesData) {
            this.parkingPassesData[item].data = [];
            this.parkingPassesData[item].total = null;
        }
    }

    toggleDetailedView(data, state: boolean): void {
        if (data && this.selectedItemData && data.id != this.selectedItemData.id) {
            if (this.currentTabId == 'rules') {
                this.selectedItemFormattedLotsArray = this.lotsParkingPassesConvertForMultiselect(data.parkingLots);
                this.selectedItemFormattedPassesArray = this.lotsParkingPassesConvertForMultiselect(data.passTypes);
            } else if (this.currentTabId == 'lots')
                this.selectedItemFormattedBuildingsArray = this.buildingParkingPassesConvertForMultiselect(data.building);
        } else if (data && !this.selectedItemData) {
            if (this.currentTabId == 'rules') {
                this.selectedItemFormattedLotsArray = this.lotsParkingPassesConvertForMultiselect(data.parkingLots);
                this.selectedItemFormattedPassesArray = this.lotsParkingPassesConvertForMultiselect(data.passTypes);
            } else if (this.currentTabId == 'lots')
                this.selectedItemFormattedBuildingsArray = this.buildingParkingPassesConvertForMultiselect(data.building);
        }
        this.selectedItemData = data ? data : null;
        this.showDetailedView = state;
        if (this.selectedItemData) {
            for (const field in this.selectedItemData.formFields)
                this.selectedItemData.formFields[field].editMode = false;
        }
    }

    showAttachment(attachment) {
        const attachmentAsObject = new Attachment(attachment.name, 0, attachment.path);
        if (attachmentAsObject.isImage()) {
            this.es.attachmentInViewAttachmentModal = attachmentAsObject;
            this.es.isViewAttachmentModalShow = true;
        } else if (attachmentAsObject.isNeedViewer()) {
            attachmentAsObject.openViewer();
        } else {
            attachmentAsObject.downloadAttachment();
        }
    }

    canEdit(buildingId?: number) {
        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {
        return this.account.canDelete(this.serviceName, buildingId);
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
        this.buldingsInMultiselectFormat = this.getBuildingsInProperFormat(this.buildings);
    }

    getBuildingsInProperFormat(arr: Array<any>) {
        return arr.map(building => ({
            id: parseInt(building.id),
            itemName: building.name,
            timeZone: (building.timeZone ? building.timeZone.value : undefined)
        }));
    }

    deleteParkingLot(lot) {
        if (!lot.id) return;
        swal(ConstantsService.getSwalConfig('Delete Parking Lot'),
            () => {
                const url = `${this.parkingPassesData.lots.remove}${lot.id}.json`;
                this.api.remove('domainParkingPass', url).subscribe(
                    () => {
                        toastr.success('Parking Lot has been deleted.');
                        if (this.parkingPassesData.lots.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete Parking Lot failed')
                );
            }
        );
    }

    deleteParkingRule(rule) {
        if (!rule.id) return;
        swal(ConstantsService.getSwalConfig('Delete Parking Rule'),
            () => {
                const url = `${this.parkingPassesData.rules.remove}${rule.id}.json`;
                this.api.remove('domainParkingPass', url).subscribe(
                    () => {
                        toastr.success('Parking Rule has been deleted.');
                        if (this.parkingPassesData.rules.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete Parking Rule failed')
                );
            }
        );
    }

    buildingParkingPassesConvertForMultiselect(buildings): any {
        let formated = [];
        if (buildings) {
            formated = buildings.map((b) => ({id: b.buildingId}));
            formated.forEach((b) => {
                this.buldingsInMultiselectFormat.forEach((msb) => {
                    if (msb.id == b.id)
                        b.itemName = msb.itemName;
                    b.timeZone = msb.timeZone;
                });
            });
        }
        return formated;
    }

    lotsParkingPassesConvertForMultiselect(lots): any {
        let formated = [];
        if (lots) {
            formated = lots.map(l => ({
                id: l.id,
                itemName: l.name
            }));
        }
        return formated;
    }

    public getUserRoles() {
        const url = `/api/v1/role.json`;
        this.api.getInfiniteList('oauthUrl', url, 1000).subscribe(
            res => {
                this.userRoles = res.entities.map(role => ({id: role.id.toString(), itemName: role.name}));
            },
            error => this.api.errorHandler(error, 'Failed get Roles')
        );
    }

    public getParkingLots() {
        this.api.get('domainParkingPass', this.parkingPassesData.lots.get).subscribe(
            res => {
                this.parkingPassesData.lots.data = [];
                const body = JSON.parse(res._body);
                this.parkingLotsForFilter = body.entities;
                this.parkingLotsInMultiselectFormat = this.parkingLotsConvertForMultiselect(body.entities);
            },
            error => this.api.errorHandler(error, 'Failed get Lots')
        );
    }

    public formatNotificationSettingsRoles(): any {
        if (this.selectedItemData) {
            this.selectedItemData.staffNotificationSettings.forEach((notificationSetting) => {
                this['pass' + (notificationSetting.action.charAt(0).toUpperCase() + notificationSetting.action.slice(1)) + 'StaffCheckbox'] = notificationSetting.enableStatus;
                notificationSetting.userRoles.forEach((role) => {
                    this[notificationSetting.action + 'StaffMultiselectArray'].push({
                        id: role.bdId,
                        itemName: role.roleName
                    });
                });
            });
            this.selectedItemData.residentsNotificationSettings.forEach((resNotification) => {
                this['pass' + (resNotification.action.charAt(0).toUpperCase() + resNotification.action.slice(1)) + 'ResidentCheckbox'] = resNotification.enableStatus;
            });
        }
    }

    emptyNotificationArrays(): void {
        this.createdStaffMultiselectArray = [];
        this.approvedStaffMultiselectArray = [];
        this.declinedStaffMultiselectArray = [];
        this.requestStaffMultiselectArray = [];
        this.passCreatedStaffCheckbox = false;
        this.passApprovedStaffCheckbox = false;
        this.passDeclinedStaffCheckbox = false;
        this.passRequestStaffCheckbox = false;
        this.passCreatedResidentCheckbox = false;
        this.passApprovedResidentCheckbox = false;
        this.passDeclinedResidentCheckbox = false;
        this.passRequestResidentCheckbox = false;
    }

    parkingLotsConvertForMultiselect(lots): any {
        let formated = [];
        if (lots) {
            formated = lots.map(l => ({
                id: l.id,
                itemName: l.name
            }));
        }
        return formated;
    }

    getTimeInZeroTimezone(date): Date {
        return new Date(new Date(date).getTime() + new Date(date).getTimezoneOffset() * 60000);
    }

    getTimeFromDateToString(date): string {
        let timeString;
        if (!isNaN(new Date(date).getTime())) {
            timeString = this.getTimeInZeroTimezone(date).getHours() + ':';
            if (this.getTimeInZeroTimezone(date).getMinutes() < 10)
                timeString += '0' + this.getTimeInZeroTimezone(date).getMinutes();
            else
                timeString += this.getTimeInZeroTimezone(date).getMinutes();
        } else
            timeString = date;

        return timeString;
    }

}



// WEBPACK FOOTER //
// /app/src/app/services/settings-parking-passes.service.ts